import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Unlock } from "lucide-react";
import { commissionsApi, type CommissionPeriodDto } from "@/api/commissions";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, Card } from "@/design-system/components/ui";
import { currencyCompact, datetime } from "@/components/commissions/formatters";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";

export function PeriodAdminPage() {
  const { user } = useAuth();
  const { tz } = useDateFormatter();
  const qc = useQueryClient();
  const periods = useQuery({
    queryKey: commissionKeys.periods(),
    queryFn: () => commissionsApi.listPeriods(),
  });

  const isAdmin = user?.roleCodes.includes("ADMIN");

  const lockOne = useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      commissionsApi.lockPeriod(year, month),
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionKeys.periods() }),
  });

  const unlockOne = useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      commissionsApi.unlockPeriod(year, month),
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionKeys.periods() }),
  });

  const now = new Date();
  const currentYear = now.getFullYear();

  const rows = useMemo(() => {
    const map = new Map<string, CommissionPeriodDto>();
    for (const p of periods.data ?? []) {
      map.set(`${p.year}-${p.month}`, p);
    }
    const result: Array<{ year: number; month: number; row: CommissionPeriodDto | undefined }> = [];
    for (let y = currentYear; y >= currentYear - 2; y--) {
      for (let m = 12; m >= 1; m--) {
        result.push({ year: y, month: m, row: map.get(`${y}-${m}`) });
      }
    }
    return result;
  }, [periods.data, currentYear]);

  return (
    <div className="flex flex-col gap-space-5">
      <div>
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
          Commissions · Admin
        </div>
        <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
          Period management
        </h1>
        <p className="text-body text-gray-mid mt-space-2 max-w-reading">
          Locking a month stops new manual claims and every claim action in that month, and refuses
          commission payments dated in it. Monthly roll-ups skip a locked month and notify
          Accounting. Only Admins can unlock a month.
        </p>
      </div>

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-small">
            <thead className="bg-onyx">
              <tr>
                <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Period
                </th>
                <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Claims
                </th>
                <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Paid
                </th>
                <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Status
                </th>
                <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Lock activity
                </th>
                <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ year, month, row }) => (
                <tr key={`${year}-${month}`} className="border-t border-border-light">
                  <td className="px-space-3 py-space-3 font-display font-semibold">
                    {year}-{String(month).padStart(2, "0")}
                  </td>
                  <td className="px-space-3 py-space-3 text-right tabular-nums text-gray-mid">
                    {row?.claimCount ?? 0}
                  </td>
                  <td className="px-space-3 py-space-3 text-right tabular-nums text-gray-mid">
                    {currencyCompact(row?.paidTotal ?? 0)}
                  </td>
                  <td className="px-space-3 py-space-3">
                    {row?.isLocked ? (
                      <span className="inline-flex items-center gap-space-1 text-status-warning font-display text-label font-semibold uppercase tracking-label">
                        <Lock size={12} strokeWidth={1.5} aria-hidden /> Locked
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-space-1 text-status-success font-display text-label font-semibold uppercase tracking-label">
                        <Unlock size={12} strokeWidth={1.5} aria-hidden /> Open
                      </span>
                    )}
                  </td>
                  {/*
                    🔴 KEYED ON isLocked, NOT ON THE PRESENCE OF A STAMP. This
                    read `row?.lockedAt ? datetime(...) : "—"`, and UnlockAsync
                    deliberately leaves LockedAt in place as audit history — so a
                    period that was locked and then unlocked drew a green "Open"
                    badge in the column to the left and a lock timestamp here,
                    with nothing saying it had since been unlocked. The unlock
                    time was carried all the way to the client (the DTO and its
                    TS mirror both have it) and rendered nowhere: `unlockedAt`
                    had ZERO production readers before this.

                    The verb is rendered beside the unlock time on purpose — a
                    bare timestamp under any header is what made the old cell
                    read as "locked at".
                  */}
                  <td className="px-space-3 py-space-3 text-gray-mid">
                    {row?.isLocked ? (
                      datetime(row.lockedAt, tz)
                    ) : row?.unlockedAt ? (
                      <span>Unlocked {datetime(row.unlockedAt, tz)}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-space-3 py-space-3 text-right">
                    {row?.isLocked ? (
                      isAdmin ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={unlockOne.isPending}
                          onClick={() => unlockOne.mutate({ year, month })}
                        >
                          Unlock
                        </Button>
                      ) : (
                        <span className="text-caption text-gray-mid">Admin only</span>
                      )
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        loading={lockOne.isPending}
                        onClick={() => lockOne.mutate({ year, month })}
                      >
                        Lock
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
